import { ChatMessage } from '@/models/Messenger';

// Window over the newest end of a group's history: messages ascending by
// created, hasMore = older messages exist below the window.
export interface MessageWindow {
  messages: ChatMessage[];
  hasMore: boolean;
}

export const byCreatedAscending = (a: ChatMessage, b: ChatMessage) => a.created.localeCompare(b.created);

// Merge the newest page into the window. No overlap with a non-empty window
// means a gap (more than a page of new messages): reset to the page.
export const mergeNewestPage = (window: MessageWindow, page: MessageWindow): MessageWindow => {
  if (!window.messages.length) return page;
  // An empty page never wipes a live window (stale snapshot during a send).
  if (!page.messages.length) return window;
  const pageIds = new Set(page.messages.map(({ messageId }) => messageId));
  const overlaps = window.messages.some(({ messageId }) => pageIds.has(messageId));
  if (!overlaps) return page;
  const messages = [
    ...window.messages.filter(({ messageId }) => !pageIds.has(messageId)),
    ...page.messages
  ].sort(byCreatedAscending);
  // hasMore refers to below the window's oldest — unchanged by the newest page.
  return { messages, hasMore: window.hasMore };
};

// Prepend an older page (cursor = window's oldest message). The exclusive
// cursor yields no duplicates; filtering is defensive.
export const prependOlderPage = (window: MessageWindow, page: MessageWindow): MessageWindow => {
  const knownIds = new Set(window.messages.map(({ messageId }) => messageId));
  const older = page.messages.filter(({ messageId }) => !knownIds.has(messageId));
  return { messages: [...older, ...window.messages], hasMore: page.hasMore };
};
